import { useCallback, useEffect, useMemo, useRef, useState } from "react";

//** COMPONENT */
import { Col, Row } from "react-bootstrap";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import TableCustom from "../../../component/table/tableCustom";
import TableRowCommon from "../../../component/table/TableRowCommon";
import InputCheckbox from "../../../component/input/inputCheckbox";
import AutocompleteSelect from "../../../component/select/autoCompleteSelect";
import Loading from "../../../component/loading/loading";
import { notiError } from "../../../component/notification/notifications";
import { swalError, swalResult } from "../../../component/notification/swal";

//** API */
import InvoiceApi, {
  InvoiceNoticeFilter,
  NoticeStallOption,
  NoticeTenantOption,
  NoticeZoneOption,
} from "../../../api/invoid/invoid.api";

//** UTILS */
import { colors } from "../../../constants/colors";
import { dateToView } from "../../../utils/date.utils";
import { numberFormat } from "../../../utils/common";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/api-error.utils";
import { stallLabel as formatStallLabel } from "../../../utils/stall.utils";

/** จำนวนที่ดึงต่อรอบ — คู่กับความสูงตาราง ~460px (จุ ~8 แถว) ให้หน้าแรกมี scrollbar เสมอเมื่อยังมีหน้าต่อไป */
const LIMIT = 20;
/** เหลืออีกกี่ px ถึงก้นกล่องแล้วเริ่มดึงหน้าถัดไป ≈ 2 แถว (~52px/แถว) */
const SCROLL_THRESHOLD_PX = 120;
/** debounce ช่องค้นหาของ dropdown */
const SEARCH_DEBOUNCE_MS = 400;

const TABLE_MAX_HEIGHT = "clamp(220px, calc(100vh - 520px), 420px)";

type FilterMode = "USER" | "ZONE" | "STALL";

type NoticeItem = {
  invoiceId: number;
  invoiceNo?: string;
  invoiceDate?: string;
  invoiceDateEnd?: string;
  zoneName?: string;
  stallNumber?: string;
  stallName?: string;
  leasesTypeNameTh?: string;
  userId?: number;
  firstname?: string;
  lastname?: string;
  /** มาจาก SUM() ของ MySQL จึงเป็น string */
  amount?: string | number;
  overdueDays?: number;
  notiMailCount?: number;
};

type Props = {
  branchId?: number;
  onClose: () => void;
};

export default function ModalNotice({ branchId, onClose }: Props) {
  const [mode, setMode] = useState<FilterMode>("USER");

  /** ---------- ค่าที่เลือก — เก็บทั้ง object เพราะต้องใช้ invoiceCount/totalAmount ทำหัวสรุปและยอดรวม ---------- */
  const [tenant, setTenant] = useState<NoticeTenantOption | null>(null);
  const [zone, setZone] = useState<NoticeZoneOption | null>(null);
  const [stall, setStall] = useState<NoticeStallOption | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(false);

  /** ---------- ตัวเลือกของ dropdown ---------- */
  const [tenantOptions, setTenantOptions] = useState<NoticeTenantOption[]>([]);
  const [zoneOptions, setZoneOptions] = useState<NoticeZoneOption[]>([]);
  const [stallOptions, setStallOptions] = useState<NoticeStallOption[]>([]);
  const [tenantSearch, setTenantSearch] = useState<string>("");
  const [zoneSearch, setZoneSearch] = useState<string>("");
  const [stallSearch, setStallSearch] = useState<string>("");
  const [loadingTenant, setLoadingTenant] = useState<boolean>(false);
  const [loadingZone, setLoadingZone] = useState<boolean>(false);
  const [loadingStall, setLoadingStall] = useState<boolean>(false);

  /** ---------- ตาราง ---------- */
  const [items, setItems] = useState<NoticeItem[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef<boolean>(false);
  const pageRef = useRef<number>(1);
  const hasMoreRef = useRef<boolean>(true);
  const reqIdRef = useRef<number>(0);

  /** ---------- ขอบเขตที่จะแจ้งเตือน ---------- */
  /** ยังไม่เลือกค่า = ยังไม่มีตาราง (ตาม mockup) */
  const scope = useMemo(() => {
    if (mode === "USER") return tenant ? { userId: Number(tenant.userId) } : null;
    if (mode === "ZONE") return zone ? { zoneId: Number(zone.zoneId) } : null;
    return stall ? { stallId: Number(stall.stallId) } : null;
  }, [mode, tenant, zone, stall]);

  /**
   * ⚠️ ตัวเดียวที่ทั้ง GET (ตาราง) และ POST (ส่งจริง) เรียกใช้ — แยกกันไม่ได้โดยโครงสร้าง
   * จึงเป็นไปไม่ได้ที่ filter ของ 2 ฝั่งจะไม่ตรงกัน ซึ่ง doc เตือนว่าถ้าส่งไม่ครบจะกลายเป็นยิงเมลทั้งระบบ
   */
  const buildFilter = useCallback((): InvoiceNoticeFilter | null => {
    if (!scope) return null;
    return { ...scope, ...(branchId ? { branchId } : {}) };
  }, [scope, branchId]);

  /** ---------- โหลดตัวเลือก dropdown ---------- */
  const loadTenants = useCallback(async (keyword: string) => {
    setLoadingTenant(true);
    try {
      const res = await InvoiceApi.findNoticeTenants({
        ...(keyword ? { keyword } : {}),
        ...(branchId ? { branchId } : {}),
      });
      setTenantOptions(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      setTenantOptions([]);
    } finally {
      setLoadingTenant(false);
    }
  }, [branchId]);

  const loadZones = useCallback(async (keyword: string) => {
    setLoadingZone(true);
    try {
      const res = await InvoiceApi.findNoticeZones({
        ...(keyword ? { keyword } : {}),
        ...(branchId ? { branchId } : {}),
      });
      setZoneOptions(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      setZoneOptions([]);
    } finally {
      setLoadingZone(false);
    }
  }, [branchId]);

  const loadStalls = useCallback(async (zoneId: number, keyword: string) => {
    setLoadingStall(true);
    try {
      const res = await InvoiceApi.findNoticeStalls({
        zoneId,
        ...(keyword ? { keyword } : {}),
        ...(branchId ? { branchId } : {}),
      });
      setStallOptions(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      setStallOptions([]);
    } finally {
      setLoadingStall(false);
    }
  }, [branchId]);

  // debounce ในตัวแบบ ModalAgent — ไม่ใช้ debouncedApiCall ที่เป็น singleton ทั้งแอป (3 ช่องจะ cancel กันเอง)
  useEffect(() => {
    if (mode !== "USER") return;
    const timer = setTimeout(() => loadTenants(tenantSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, tenantSearch, loadTenants]);

  // โซนใช้ทั้ง mode "ทั้งโซน" และเป็นขั้นแรกของ mode "รายแผงค้า"
  useEffect(() => {
    if (mode === "USER") return;
    const timer = setTimeout(() => loadZones(zoneSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, zoneSearch, loadZones]);

  useEffect(() => {
    if (mode !== "STALL" || !zone?.zoneId) {
      setStallOptions([]);
      return;
    }
    const zoneId = Number(zone.zoneId);
    const timer = setTimeout(() => loadStalls(zoneId, stallSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, zone, stallSearch, loadStalls]);

  /** ---------- reset ---------- */
  const handleChangeMode = (nextMode: FilterMode) => {
    setMode(nextMode);
    setTenant(null);
    setZone(null);
    setStall(null);
    setTenantSearch("");
    setZoneSearch("");
    setStallSearch("");
  };

  const handleChangeZone = (value: NoticeZoneOption | null) => {
    setZone(value);
    // เปลี่ยนโซนต้องล้างแผงค้าที่เลือกไว้ ไม่งั้นจะค้างเป็นแผงของโซนเดิม
    setStall(null);
    setStallSearch("");
  };

  /** ---------- ตาราง ---------- */
  const mergeUnique = (prev: NoticeItem[], next: NoticeItem[]): NoticeItem[] => {
    const seen = new Set<number>(prev.map((item) => Number(item.invoiceId)));
    return prev.concat(next.filter((item) => !seen.has(Number(item.invoiceId))));
  };

  const fetchPage = useCallback(
    async (targetPage: number, reqId: number) => {
      const filter = buildFilter();
      if (!filter || fetchingRef.current) return;
      fetchingRef.current = true;
      setIsLoading(true);
      try {
        const res = await InvoiceApi.findAllNotice({ ...filter, page: targetPage, limit: LIMIT });
        if (reqId !== reqIdRef.current) return; // scope เปลี่ยนไประหว่างรอ → ทิ้ง response นี้
        const payload = res?.data?.data;
        const list: NoticeItem[] = payload?.items || [];
        const meta = payload?.meta;
        setTotalItems(Number(meta?.totalItems || 0));
        setItems((prev) => (targetPage === 1 ? list : mergeUnique(prev, list)));
        pageRef.current = targetPage;
        hasMoreRef.current =
          list.length > 0 &&
          Number(meta?.currentPage || targetPage) < Number(meta?.totalPages || 0);
      } catch (error) {
        if (reqId !== reqIdRef.current) return;
        hasMoreRef.current = false; // หยุดยิงซ้ำเรื่อยๆ ตอน error
        notiError(getApiErrorMessage(error, "ดึงรายการค้างชำระไม่สำเร็จ"));
      } finally {
        if (reqId === reqIdRef.current) setIsLoading(false);
        fetchingRef.current = false;
      }
    },
    [buildFilter]
  );

  const filterKey = useMemo(
    () => JSON.stringify({ scope, branchId, refreshTick }),
    [scope, branchId, refreshTick]
  );

  useEffect(() => {
    reqIdRef.current += 1;
    pageRef.current = 1;
    fetchingRef.current = false;
    setItems([]);
    setTotalItems(0);
    // ล้างการยืนยันทุกครั้งที่ขอบเขตเปลี่ยน — กันเคสติ๊กยืนยันของโซน A แล้วสลับไปโซน B แต่ปุ่มยังเปิดอยู่
    setConfirmed(false);
    if (containerRef.current) containerRef.current.scrollTop = 0;
    if (!scope) {
      hasMoreRef.current = false;
      setIsLoading(false);
      return;
    }
    hasMoreRef.current = true;
    fetchPage(1, reqIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  /**
   * ห้ามใส่ throttle ที่นี่ — throttle แบบ leading-edge จะกลืน scroll event ตัวสุดท้ายของชุด
   * ซึ่งเป็นตัวที่ scrollTop แตะก้นจริง ทำให้ต้องขยับ scroll ซ้ำมันจึงโหลด (เกิดเป็นบางรอบตามจังหวะเวลา)
   * เช็ค guard ที่ถูกที่สุดก่อน จึงไม่อ่าน layout เลยตอนกำลังโหลดหรือหมดหน้าแล้ว
   */
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (fetchingRef.current || !hasMoreRef.current) return;
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD_PX) {
        fetchPage(pageRef.current + 1, reqIdRef.current);
      }
    },
    [fetchPage]
  );

  /** เช็คซ้ำหลังโหลดเสร็จว่ายังอยู่ติดก้นกล่องไหม กันเคสที่ scroll event จะไม่มาอีก */
  useEffect(() => {
    if (isLoading || !hasMoreRef.current || !scope) return;
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD_PX) {
      fetchPage(pageRef.current + 1, reqIdRef.current);
    }
  }, [items, isLoading, scope, fetchPage]);

  /** ---------- ส่งการแจ้งเตือน ---------- */
  const handleSend = async () => {
    const filter = buildFilter();
    // guard สุดท้าย — selectAll คู่กับ filter ว่างแปลว่ายิงเมลทั้งระบบ
    if (!filter || !confirmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await InvoiceApi.sendNotice({ selectAll: true, filter, remark: "" });
      const data = res?.data?.data;
      if (!data) throw new Error("EMPTY_RESPONSE");

      // HTTP 200 ไม่ได้แปลว่าสำเร็จทุกใบ — ต้องอ่านตัวเลขสรุป
      const total = Number(data.total || 0);
      const success = Number(data.success || 0);
      const letters = Number(data.letters || 0);
      const failed = Number(data.failed || 0);
      const skipped = Number(data.skipped || 0);
      const notFound = Number(data.notFound || 0);

      const results: any[] = Array.isArray(data.results) ? data.results : [];

      // เก็บ exception ดิบไว้ที่ console ให้ dev ตามต่อ — log เฉพาะข้อความ error
      // ไม่ log ทั้ง results เพราะมีอีเมลผู้เช่าอยู่ด้วย (PDPA)
      const rawReasons: string[] = [];
      results.forEach((row: any) => {
        const reason = String(row?.errorMessage || "").trim();
        if (reason && rawReasons.indexOf(reason) === -1) rawReasons.push(reason);
      });
      if (rawReasons.length > 0) console.error("[invoice/notice]", rawReasons);

      // success นับเป็น "ใบ" ส่วน letters นับเป็น "ฉบับ/อีเมล" — คนละหน่วย ต้องเขียนกำกับให้ชัด
      const headline =
        success > 0
          ? `ส่งแจ้งเตือนสำเร็จ ${numberFormat(success, 0, 0)} ใบ (หนังสือ ${numberFormat(letters, 0, 0)} ฉบับ)`
          : "ไม่มีรายการที่ถูกส่งแจ้งเตือน";
      const allOk = success > 0 && failed === 0 && skipped === 0 && notFound === 0;
      const icon: "success" | "warning" | "error" = allOk ? "success" : success > 0 ? "warning" : "error";

      swalResult(icon, headline, buildResultHtml({ total, letters, success, failed, skipped, notFound }, results), () => {
        // ปิด modal เฉพาะตอนที่มีอย่างน้อย 1 ใบส่งออกไปจริง
        // ถ้าล้มหมดให้คาไว้จะได้กดซ้ำโดยไม่ต้องเลือกขอบเขตใหม่
        if (success > 0) {
          onClose();
        } else {
          setConfirmed(false);
          setRefreshTick((tick) => tick + 1);
        }
      });
    } catch (error) {
      swalError(getApiErrorMessage(error, "ส่งแจ้งเตือนไม่สำเร็จ"));
      // ทุกใบที่เลือกไม่ใช่บิลค้างชำระแล้ว → คาไว้ที่ modal แล้วดึงรายการใหม่ให้
      if (getApiErrorStatus(error) === 404) setRefreshTick((tick) => tick + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** ---------- ข้อมูลสรุปของขอบเขตที่เลือก ---------- */
  const scopeOption: NoticeTenantOption | NoticeZoneOption | NoticeStallOption | null =
    mode === "USER" ? tenant : mode === "ZONE" ? zone : stall;

  const scopeLabel = useMemo(() => {
    if (mode === "USER" && tenant) {
      return [tenant.fullname || "-", tenant.idCard ? `เลขบัตร ${tenant.idCard}` : ""]
        .filter(Boolean)
        .join(" · ");
    }
    if (mode === "ZONE" && zone) return zone.zoneName || "-";
    if (mode === "STALL" && stall) {
      // stall.stallLabel เป็นป้ายที่ backend ประกอบมาแบบ "เลข - ชื่อ" ซึ่งคนละรูปแบบกับมาตรฐาน
      // ประกอบเองจาก field ดิบก่อน แล้วค่อยตกไปใช้ของ backend ถ้าไม่มีทั้งเลขและชื่อ
      return formatStallLabel(stall, "") || stall.stallLabel || "-";
    }
    return "";
  }, [mode, tenant, zone, stall]);

  // totalItems มาจาก meta ของ query จริงจึงตรงกับตารางเสมอ ส่วน invoiceCount ใช้ตอนยังโหลดไม่เสร็จ
  const displayCount = totalItems || Number(scopeOption?.invoiceCount || 0);
  const displayTotalAmount = Number(scopeOption?.totalAmount || 0);

  /** ---------- คอลัมน์ ---------- */
  const headCells = [
    { id: "notiMailCount", align: "center", label: "จำนวนครั้งที่แจ้ง" },
    { id: "invoiceDate", align: "center", label: "วันที่เอกสาร" },
    { id: "invoiceNo", align: "center", label: "เลขที่บิล" },
    { id: "tenantName", align: "center", label: "ชื่อผู้เช่า" },
    { id: "zoneName", align: "center", label: "โซน" },
    { id: "stallName", align: "center", label: "แผงค้า" },
    { id: "stallNumber", align: "center", label: "เลขที่แผง" },
    { id: "leasesTypeNameTh", align: "center", label: "ประเภทหนี้" },
    { id: "invoiceDateEnd", align: "center", label: "กำหนดวันชำระ" },
    { id: "overdueDays", align: "center", label: "ค้าง (วัน)" },
    { id: "amount", align: "center", label: "จำนวนเงิน" },
  ];

  const renderRow = (objData: NoticeItem) => {
    const invoiceId = Number(objData.invoiceId);
    const fullname = `${objData.firstname || ""} ${objData.lastname || ""}`.trim();
    const objRenderData = {
      key: String(invoiceId),
      id: String(invoiceId),
      obj: objData,
      columns: [
        { option: "TEXT", align: "center", label: Number(objData.notiMailCount || 0) },
        { option: "TEXT", align: "center", label: objData.invoiceDate ? dateToView(objData.invoiceDate) : "-" },
        { option: "TEXT", align: "center", label: objData.invoiceNo || "-" },
        { option: "TEXT", align: "center", label: fullname || "-" },
        { option: "TEXT", align: "center", label: objData.zoneName || "-" },
        { option: "TEXT", align: "center", label: objData.stallName || "-" },
        { option: "TEXT", align: "center", label: objData.stallNumber || "-" },
        { option: "TEXT", align: "center", label: objData.leasesTypeNameTh || "-" },
        {
          option: "TEXT",
          align: "center",
          label: objData.invoiceDateEnd ? dateToView(objData.invoiceDateEnd) : "-",
          style: { color: colors.red },
        },
        {
          option: "TEXT",
          align: "center",
          label: Number(objData.overdueDays || 0),
          style: { color: colors.red },
        },
        { option: "TEXT", align: "right", label: numberFormat(Number(objData.amount || 0)) },
      ],
    };
    return <TableRowCommon {...objRenderData} />;
  };

  /** ยัด loading/end row ท้าย rowsData เพื่อไม่ให้ flash "ไม่พบข้อมูล" ตอนโหลดครั้งแรก */
  const rowsData: any[] = items.map((item) => renderRow(item));
  if (isLoading) {
    rowsData.push(
      <TableRow key="__loading">
        <TableCell colSpan={headCells.length} align="center" sx={{ py: 2, color: colors.textLightGray }}>
          กำลังโหลด...
        </TableCell>
      </TableRow>
    );
  }

  /** ---------- dropdown ตาม mode ---------- */
  const renderModeSelector = () => {
    if (mode === "ZONE") {
      return (
        <Col className="my-2" lg={6} md={12} xs={12}>
          <AutocompleteSelect
            labelId="notice-zone"
            heading="ค้นหาโซน"
            required
            placeholder="เลือกโซน"
            options={zoneOptions || []}
            loading={loadingZone}
            noOptionsText="ไม่พบโซนที่มีบิลค้างชำระ"
            getOptionLabel={(option: any) => option?.zoneName || "-"}
            filterOptions={(option: any) => `${option?.zoneName || ""}`}
            isOptionEqualToValue={(option: any, value: any) => option?.zoneId === value?.zoneId}
            renderOption={(optionProps: any, option: any) => (
              <li {...optionProps} key={option?.zoneId}>
                {renderOptionRow(option?.zoneName || "-", option)}
              </li>
            )}
            value={zone}
            onInputChange={(_: any, value: any) => setZoneSearch(value || "")}
            onChange={(_: any, value: any) => setZone(value || null)}
          />
        </Col>
      );
    }

    if (mode === "STALL") {
      return (
        <>
          <Col className="my-2" lg={6} md={12} xs={12}>
            <AutocompleteSelect
              labelId="notice-stall-zone"
              heading="โซน"
              required
              placeholder="เลือกโซน"
              options={zoneOptions || []}
              loading={loadingZone}
              noOptionsText="ไม่พบโซนที่มีบิลค้างชำระ"
              getOptionLabel={(option: any) => option?.zoneName || "-"}
              filterOptions={(option: any) => `${option?.zoneName || ""}`}
              isOptionEqualToValue={(option: any, value: any) => option?.zoneId === value?.zoneId}
              renderOption={(optionProps: any, option: any) => (
                <li {...optionProps} key={option?.zoneId}>
                  {renderOptionRow(option?.zoneName || "-", option)}
                </li>
              )}
              value={zone}
              onInputChange={(_: any, value: any) => setZoneSearch(value || "")}
              onChange={(_: any, value: any) => handleChangeZone(value || null)}
            />
          </Col>
          <Col className="my-2" lg={6} md={12} xs={12}>
            <AutocompleteSelect
              labelId="notice-stall"
              heading="แผงค้า"
              required
              placeholder={zone ? "เลือกแผงค้า" : "เลือกโซนก่อน"}
              disabled={!zone}
              options={stallOptions || []}
              loading={loadingStall}
              noOptionsText="ไม่พบแผงค้าที่มีบิลค้างชำระในโซนนี้"
              getOptionLabel={(option: any) =>
                formatStallLabel(option, "") || option?.stallLabel || "-"
              }
              filterOptions={(option: any) =>
                `${option?.stallLabel || ""} ${option?.stallNumber || ""} ${option?.stallName || ""}`
              }
              isOptionEqualToValue={(option: any, value: any) => option?.stallId === value?.stallId}
              renderOption={(optionProps: any, option: any) => (
                <li {...optionProps} key={option?.stallId}>
                  {renderOptionRow(
                    formatStallLabel(option, "") || option?.stallLabel || "-",
                    option
                  )}
                </li>
              )}
              value={stall}
              onInputChange={(_: any, value: any) => setStallSearch(value || "")}
              onChange={(_: any, value: any) => setStall(value || null)}
            />
          </Col>
        </>
      );
    }

    return (
      <Col className="my-2" lg={12} md={12} xs={12}>
        <AutocompleteSelect
          labelId="notice-tenant"
          heading="ค้นหาผู้เช่า"
          required
          placeholder="พิมพ์ชื่อผู้เช่าหรือเลขบัตรประจำตัวประชาชน"
          options={tenantOptions || []}
          loading={loadingTenant}
          noOptionsText="ไม่พบผู้เช่าที่มีบิลค้างชำระ"
          getOptionLabel={(option: any) =>
            [option?.fullname || "-", option?.idCard].filter(Boolean).join(" · ")
          }
          // ต้องส่งเสมอ — wrapper hard-wire createFilterOptions ถ้าไม่ส่ง ผลที่ server หาเจอด้วยเลขบัตรจะถูกกรองทิ้งฝั่ง client
          filterOptions={(option: any) => `${option?.fullname || ""} ${option?.idCard || ""}`}
          isOptionEqualToValue={(option: any, value: any) => option?.userId === value?.userId}
          renderOption={(optionProps: any, option: any) => (
            <li {...optionProps} key={option?.userId}>
              {renderOptionRow(option?.fullname || "-", option, option?.idCard)}
            </li>
          )}
          value={tenant}
          onInputChange={(_: any, value: any) => setTenantSearch(value || "")}
          onChange={(_: any, value: any) => setTenant(value || null)}
        />
      </Col>
    );
  };

  return (
    <>
      {/* Loading zIndex 2500 อยู่เหนือ modal จึงคลุมทั้งจอได้ */}
      {isSubmitting && <Loading show />}
      <ModalCustom
        // ต้อง true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // MUI Autocomplete render popper ผ่าน portal ออกนอก modal — ถ้า enforceFocus จะแย่ง focus กลับ
        enforceFocus={false}
        // ปิด modal เองผ่าน onClose ไม่ให้ไป stomp redux modal.isShow ของหน้าอื่น
        autoResetModal={false}
        size="xl"
        closeButton
        alignFooter="end"
        footerInline
        title="แจ้งเตือนการค้างชำระ"
        onClose={onClose}
        onReset={onClose}
        textBtnCancel="ยกเลิก"
        textBtnConfirm="ส่งการแจ้งเตือน"
        onSubmit={handleSend}
        disabledSubmit={!scope || !confirmed || isSubmitting}
        component={
          <Box sx={{ px: 3 }}>
            <Box sx={{ mb: 1 }}>
              แจ้งเตือนตาม <span style={{ color: colors.red }}>*</span>
            </Box>
            <FormControl sx={{ mb: 1 }}>
              <RadioGroup
                row
                name="notice-filter-mode"
                value={mode}
                onChange={(event) => handleChangeMode(event.target.value as FilterMode)}
              >
                <FormControlLabel value="USER" control={<Radio />} label="ผู้เช่า" />
                <FormControlLabel value="ZONE" control={<Radio />} label="ทั้งโซน" />
                <FormControlLabel value="STALL" control={<Radio />} label="รายแผงค้า" />
              </RadioGroup>
            </FormControl>

            <Row>{renderModeSelector()}</Row>

            {scope && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{scopeLabel}</Typography>
                  <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
                    {numberFormat(displayCount, 0, 0)} บิลค้างชำระ
                  </Typography>
                </Box>

                <TableCustom
                  headCells={headCells}
                  rowsData={rowsData}
                  customScroll
                  hidePagination
                  stickyHeader
                  tableMaxHeight={TABLE_MAX_HEIGHT}
                  onScroll={handleScroll}
                  containerRef={containerRef}
                  tableMinWidth={1400}
                  page={1}
                  pageLimit={LIMIT}
                  rowCount={totalItems}
                  sortBy=""
                  sortType="ASC"
                  onSort={() => {}}
                  setPage={() => {}}
                  setPageLimit={() => {}}
                  textEmptyData="ไม่พบรายการค้างชำระ"
                />

                {/* render นอกตารางเพราะ TableCustom ไม่มี slot footer และยอดรวมต้องเห็นตลอดแม้ scroll */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    backgroundColor: colors.extraLightGray,
                    borderBottomLeftRadius: "10px",
                    borderBottomRightRadius: "10px",
                  }}
                >
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>ยอดค้างชำระรวม</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                    {numberFormat(displayTotalAmount)}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <InputCheckbox
                label="ยืนยันการทำรายการแจ้งเตือนการค้างชำระตามรายการข้างต้น"
                checked={confirmed}
                disabled={!scope || isSubmitting}
                onChange={(event: any) => setConfirmed(event.target.checked)}
              />
            </Box>
          </Box>
        }
      />
    </>
  );
}

/**
 * swal รับ `html` เป็น string แล้ว render ดิบ — ค่าที่มาจาก server (ชื่อ/อีเมล/errorMessage)
 * ต้อง escape ก่อนเสมอ ไม่งั้นเป็นช่องทาง XSS
 */
const escapeHtml = (value: any): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** PDPA: response คืนอีเมลดิบมา ไม่ควรแสดงเต็ม */
const maskEmail = (email?: string | null): string => {
  if (!email) return "-";
  const parts = String(email).split("@");
  if (parts.length !== 2 || !parts[0]) return "-";
  const name = parts[0];
  const visible = name.slice(0, 2);
  return `${visible}${"*".repeat(Math.max(name.length - visible.length, 1))}@${parts[1]}`;
};

/**
 * exception ดิบของ Node (ENOENT ... ortorkor-logo.png) ผู้ใช้อ่านไม่รู้เรื่อง
 * และไม่ควรโชว์ path ในเครื่อง server ออกหน้าจอ — แปลงเป็นข้อความกลาง ตัวเต็มอยู่ที่ console
 */
const toReadableReason = (message?: string): string => {
  const text = String(message || "").trim();
  if (!text) return "";
  const isSystemError =
    /ENOENT|ECONN|EACCES|ETIMEDOUT|ESOCKET|EPIPE|no such file|Cannot read|is not defined|^\s*[A-Z][a-zA-Z]*Error\b|[A-Za-z]:\\|\/[a-z]+\//.test(
      text
    );
  return isSystemError ? "ระบบสร้างไฟล์หนังสือไม่สำเร็จ กรุณาแจ้งผู้ดูแลระบบ" : text;
};

const SEND_STATUS_VIEW: Record<string, { label: string; color: string }> = {
  SUCCESS: { label: "ส่งสำเร็จ", color: colors.success },
  FAILED: { label: "ส่งไม่สำเร็จ", color: colors.red },
  SKIPPED: { label: "ข้าม (ไม่มีอีเมล)", color: colors.warning },
};

/** กล่องสรุปผลใน swal — ตัวเลขรวมด้านบน + รายการรายใบด้านล่าง */
function buildResultHtml(
  summary: { total: number; letters: number; success: number; failed: number; skipped: number; notFound: number },
  results: any[]
): string {
  const chip = (label: string, value: number, color: string) => `
    <div style="flex:1;min-width:88px;border:1px solid ${color};border-radius:8px;padding:6px 8px;text-align:center;">
      <div style="font-size:13px;color:${colors.textLightGray};">${label}</div>
      <div style="font-size:18px;font-weight:600;color:${color};">${numberFormat(value, 0, 0)}</div>
    </div>`;

  const chips = `
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;">
      ${chip("ทั้งหมด (ใบ)", summary.total, colors.textLightGray)}
      ${chip("หนังสือ (ฉบับ)", summary.letters, colors.textLightGray)}
      ${chip("สำเร็จ", summary.success, colors.success)}
      ${chip("ไม่สำเร็จ", summary.failed, colors.red)}
      ${chip("ข้าม", summary.skipped, colors.warning)}
      ${chip("ไม่พบบิล", summary.notFound, colors.textLightGray)}
    </div>`;

  const rows = results
    .map((row: any) => {
      const view = SEND_STATUS_VIEW[String(row?.sendStatus)] || {
        label: String(row?.sendStatus || "-"),
        color: colors.textLightGray,
      };
      const reason = toReadableReason(row?.errorMessage);
      return `
        <div style="display:flex;gap:10px;align-items:flex-start;padding:8px 0;border-bottom:1px solid ${colors.lightGray};">
          <div style="flex:0 0 108px;font-size:13px;text-align:center;padding:2px 6px;border-radius:4px;color:#fff;background:${view.color};">
            ${escapeHtml(view.label)}
          </div>
          <div style="flex:1;min-width:0;text-align:left;">
            <div style="font-size:15px;">เลขที่บิล ${escapeHtml(row?.invoiceNo || "-")}</div>
            <div style="font-size:13px;color:${colors.textLightGray};">
              อีเมล : ${escapeHtml(maskEmail(row?.email))} · แจ้งแล้ว ${numberFormat(Number(row?.notiMailCount || 0), 0, 0)} ครั้ง
            </div>
            ${reason ? `<div style="font-size:13px;color:${colors.red};">${escapeHtml(reason)}</div>` : ""}
          </div>
        </div>`;
    })
    .join("");

  const notFoundNote =
    summary.notFound > 0
      ? `<div style="margin-top:10px;padding:8px;border-radius:8px;background:${colors.red_10};color:${colors.red};font-size:14px;text-align:left;">
           มี ${numberFormat(summary.notFound, 0, 0)} ใบที่ไม่ใช่บิลค้างชำระแล้ว (อาจถูกชำระไประหว่างเปิดหน้านี้ค้างไว้)
         </div>`
      : "";

  return `${chips}
    <div style="max-height:280px;overflow-y:auto;text-align:left;">${rows}</div>
    ${notFoundNote}`;
}

/** option row: ชื่อชิดซ้าย รายละเอียดชิดขวา */
function renderOptionRow(label: string, option: any, idCard?: string) {
  const detail = [
    idCard,
    `${numberFormat(Number(option?.invoiceCount || 0), 0, 0)} ใบ`,
    `${numberFormat(Number(option?.totalAmount || 0))} บาท`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 2 }}>
      <Typography sx={{ fontSize: 16, flex: 1, minWidth: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
        {detail}
      </Typography>
    </Box>
  );
}
